import { AxiosHeaders } from "axios"
import { CompanyDataColumns, CompanyJson, ContractStatus } from "./types"
import { inspect } from "./inspect"
import log from 'loglevel'
import { bpoApiRequest, getCustomHeaders } from "../Admin/functions"
import { find, isNil, map } from "lodash/fp"
import * as Xlsx from "xlsx"

type EmployeeExcel = {
    data: any
}

export const getCompanies = async (): Promise<CompanyDataColumns[] | undefined> => {
    const result = await bpoApiRequest('GET', `/v1/admin/companies`)
    log.debug(`[functions.getCompanies] ${inspect({ result })}`)
    if (isNil(result)) {
        log.error('[functions.getCompanies] Http request error.')
        return undefined
    }
    if (result.status === 200) {
        const converted = map((row: CompanyJson) => {
            const {
                closingDate,
                name,
                memberId,
                sub,
                code,
                autoPayment,
                contractStatus
            } = row
            return {
                id: memberId,
                closingDate,
                name,
                sub,
                code,
                autoPayment,
                contractStatus
            }
        })(result.data as CompanyJson[])
        return converted
    }
    return undefined
}

export const doDownload = async (
    key: string,
    fileName: string,
    companyName: string
): Promise<any> => {
    const customHeaders = getCustomHeaders() as AxiosHeaders | undefined
    if (isNil(customHeaders)) {
        return undefined
    }
    const result = await bpoApiRequest('POST',
        `/v1/admin/download`,
        {
            key: `${key}/master/master.json`,
            companyName
        }
    )
    if (isNil(result)) {
        return undefined
    }
    const buffer = Buffer.from(result.data, 'base64')
    const url = window.URL.createObjectURL(new Blob([buffer]))
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.click()
    a.remove()
    return result.data
}

export const xlsx2Json = async (excel: File, sheetName?: string): Promise<string> => {
    const uploadFileBuffer = await excel.arrayBuffer()
    const workbook = Xlsx.read(uploadFileBuffer, { type: 'buffer', bookVBA: true })
    let worksheet = workbook.Sheets[workbook.SheetNames[0]]
    if (!isNil(sheetName)) {
        worksheet = workbook.Sheets[sheetName]
    }
    const excelJson = Xlsx.utils.sheet_to_json(worksheet)
    const employeeExcelData: EmployeeExcel = {
        data: excelJson
    }
    const jsonData = JSON.stringify(employeeExcelData)
    return jsonData
}

export const doUpload = async (
    memberId: string,
    file: string,
    companyName: string
): Promise<number | undefined> => {
    const customHeaders = getCustomHeaders() as AxiosHeaders | undefined
    if (isNil(customHeaders)) {
        return undefined
    }
    const result = await bpoApiRequest('POST',
        `/v1/admin/upload`,
        {
            memberId,
            fileData: file,
            companyName
        }
    )
    log.debug(`[functions.doUpload] ${inspect({ result })}`)
    return result?.status
}

export const putCompanies = async (companiesJson: CompanyJson[]): Promise<boolean> => {
    const result = await bpoApiRequest('PUT', `/v1/admin/companies`, companiesJson)
    if (isNil(result)) {
        log.error('[functions.getRows] Http request error.')
        return false
    }
    return result.data as boolean
}

export const doCancel = async (memberId: string, rows: CompanyDataColumns[]): Promise<boolean> => {
    const company = find((company: CompanyDataColumns) => company.id === memberId)(rows)
    if (isNil(company) || company.contractStatus === ContractStatus.inactive) {
        return false
    }
    const companiesJson: CompanyJson[] = map((row: CompanyDataColumns) => {
        const {
            id,
            contractStatus,
            name,
            autoPayment,
            closingDate,
            sub,
            code
        } = row
        return {
            memberId: id,
            name,
            autoPayment,
            closingDate,
            sub,
            code,
            contractStatus: id === memberId ? ContractStatus.inactive : contractStatus
        }
    })(rows)
    const result = await putCompanies(companiesJson)
    return result
}

export const unDoCancel = async (memberId: string, rows: CompanyDataColumns[]): Promise<boolean> => {
    const company = find((company: CompanyDataColumns) => company.id === memberId)(rows)
    if (isNil(company) || company.contractStatus === ContractStatus.active) {
        return false
    }
    const companiesJson: CompanyJson[] = map((row: CompanyDataColumns) => {
        const {
            id,
            contractStatus,
            name,
            autoPayment,
            closingDate,
            sub,
            code
        } = row
        return {
            memberId: id,
            name,
            autoPayment,
            closingDate,
            sub,
            code,
            contractStatus: id === memberId ? ContractStatus.active : contractStatus
        }
    })(rows)
    const result = await putCompanies(companiesJson)
    return result
}
